/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import {
  Heading,
  Item,
  Switch,
  Text,
  ToggleButton,
  ListView,
  ActionGroup,
} from '@adobe/react-spectrum'
import { useAtom } from 'jotai'
import { A3dHotSpot, A3dViewerElement } from '@a3d-viewer/viewer'

import Pin from '@spectrum-icons/workflow/PinOff'
import Focus from '@spectrum-icons/workflow/Target'
import Delete from '@spectrum-icons/workflow/Delete'

import { viewerAtom } from '../config'
import { FormItem } from '../components/FormItem'

type HotSpotsPanelProps = {
  viewerEle: A3dViewerElement | null
}

function HotSpotsPanel({ viewerEle }: HotSpotsPanelProps) {
  const [atom, setAtom] = useAtom(viewerAtom)

  const setAddHotSpotWithCameraOrbit = (value: boolean) => {
    setAtom((prev) => ({
      ...prev,
      hotSpot: {
        ...prev.hotSpot,
        addHotSpotWithCameraOrbit: value,
      },
    }))
  }

  const setAddHotSpotOnNextClick = () => {
    setAtom((prev) => ({
      ...prev,
      hotSpot: {
        ...prev.hotSpot,
        addHotSpotOnNextClick: !prev.hotSpot.addHotSpotOnNextClick,
      },
    }))
  }

  let hotSpots: A3dHotSpot[] = []
  if (atom.hotSpot.a3dHotSpotsProps.length > 0 && viewerEle) {
    hotSpots = [...viewerEle.querySelectorAll('a3d-hot-spot')] as A3dHotSpot[]
  }

  return (
    <>
      <Heading level={1}>Hot Spots</Heading>
      <FormItem>
        <ToggleButton
          isSelected={atom.hotSpot.addHotSpotOnNextClick}
          onChange={setAddHotSpotOnNextClick}
          aria-label="Pin"
        >
          <Pin />
          <Text>Add hot spot</Text>
        </ToggleButton>
      </FormItem>
      <FormItem>
        <Switch
          isSelected={atom.hotSpot.addHotSpotWithCameraOrbit}
          onChange={setAddHotSpotWithCameraOrbit}
        >
          Add hot spot with camera orbit
        </Switch>
      </FormItem>

      {hotSpots.length > 0 && (
        <>
          <Heading level={5} UNSAFE_className="m-0 p-0">
            Focus Hot Spots
          </Heading>
          <ListView aria-label="HotSpots">
            {
              hotSpots.map((hotSpot, index) => {
                return (
                  <Item
                    key={`hotSpotsPanel-a3dHotSpot-${index}`}
                    textValue={hotSpot.id}
                  >
                    <Text>{hotSpot.id}</Text>
                    <ActionGroup
                      onAction={(key) => {
                        if (key === 'focus') {
                          viewerEle?.focusHotSpot(hotSpot)
                          return
                        }
                        if (key === 'delete') {
                          atom.hotSpot.a3dHotSpotsProps.splice(index, 1)
                          setAtom({
                            ...atom,
                            hotSpot: {
                              ...atom.hotSpot,
                              a3dHotSpotsProps: [
                                ...atom.hotSpot.a3dHotSpotsProps,
                              ],
                            },
                          })
                          return
                        }
                      }}
                    >
                      <Item key="focus" textValue="Focus">
                        <Focus />
                      </Item>
                      <Item key="delete" textValue="Delete">
                        <Delete />
                      </Item>
                    </ActionGroup>
                  </Item>
                )
              }) as any
            }
          </ListView>
        </>
      )}
    </>
  )
}

export { HotSpotsPanel }
